/* External */
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import debounce from 'lodash-es/debounce';

/* Utils */
import useShowAndHide from '../../../../hooks/useShowAndHide';
import { useIsCursorVisible } from '../../../../providers/CursorStateProvider';
import platform from '../../../../utils/platform';
import { useOnKeyDown } from '../../../../utils/keyDownEvents';
import useRefSync from '../../../../hooks/useRefSync';
import { gaService } from '../../../../services/gaService';
import { REPORT_GA } from '../../../../utils/constants';

const DIRECTIONAL_KEYS = Object.values(platform.keyCodes.navigation);
const AUTOHIDE_CONTROLS_MS = 5000;
const AUTOHIDE_CONTROLS_WHEN_PAUSING = 0;

export default function useControlsVisibility({
  handleUserInteraction,
  isPlaying,
  pauseOnShowControls,
  pause,
  displayingNotMainControls,
}) {
  const [isMouseHoverOnControls, setIsMouseHoverOnControls] = useState(false);

  const {
    mounted: mountedControls,
    displaying: displayingControls,
    show: showControlsBasic,
    hide: hideControls,
  } = useShowAndHide(false);

  const displayingControlsRef = useRefSync(displayingControls);
  const isPlayingRef = useRefSync(isPlaying);

  const handleMouseEnter = useCallback(() => {
    setIsMouseHoverOnControls(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsMouseHoverOnControls(false);
  }, []);

  const isMagicRemoteActive = useIsCursorVisible();

  const debouncedHideControls = useMemo(() => debounce(
    hideControls,
    pauseOnShowControls ? AUTOHIDE_CONTROLS_WHEN_PAUSING : AUTOHIDE_CONTROLS_MS,
  ), [hideControls, pauseOnShowControls]);

  const showControls = useCallback((withoutAnimation) => {
    if (pauseOnShowControls && isPlayingRef.current) {
      pause();
      showControlsBasic(withoutAnimation);
      isPlayingRef.current = false;
    }
    if (!pauseOnShowControls) {
      showControlsBasic(withoutAnimation);
    }
    if (isPlayingRef.current) debouncedHideControls();
  }, [
    pauseOnShowControls,
    isPlayingRef,
    showControlsBasic,
    debouncedHideControls,
    pause,
  ]);

  const showControlsAndReport = useCallback((withoutAnimation) => {
    if (!displayingControlsRef.current) {
      gaService.sendEvent({
        action: REPORT_GA.GA_EVENT_EVENT_PLAYER_CONTROLS_SHOWS,
        label: REPORT_GA.GA_EVENT_LABEL_PLAYER_CONTROLS_SHOWS,
        category: REPORT_GA.GA_EVENT_CATEGORY_VOD_PLAYER,
      });
    }
    showControls(withoutAnimation);
  }, [displayingControlsRef, showControls]);

  const handleDirectionalKeyDown = useCallback(() => {
    handleUserInteraction();
    showControlsAndReport();
    return true;
  }, [handleUserInteraction, showControlsAndReport]);

  useOnKeyDown({
    listener: handleDirectionalKeyDown,
    forKeyCodes: DIRECTIONAL_KEYS,
    when: !displayingNotMainControls,
  });

  useEffect(() => {
    if (isPlaying && (!isMouseHoverOnControls || pauseOnShowControls)) {
      debouncedHideControls();
    }
    return () => {
      debouncedHideControls.cancel();
    };
  }, [
    isPlaying,
    isMouseHoverOnControls,
    debouncedHideControls,
    pauseOnShowControls,
    showControls,
    displayingNotMainControls,
  ]);

  useEffect(() => {
    if (!isMagicRemoteActive) {
      setIsMouseHoverOnControls(false);
    }
  }, [isMagicRemoteActive, setIsMouseHoverOnControls]);

  return {
    mountedControls,
    displayingControls,
    showControls,
    showControlsAndReport,
    hideControls,
    handleMouseEnter,
    handleMouseLeave,
  };
}
